import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MapPin, Store as StoreIcon, ArrowUpRight, Search } from "lucide-react";

const CP_KEY = "cliente_cp_v1";
const RANGOS = [5, 10, 20];

export const ComerciosCercanos = () => {
  const [cp, setCp] = useState(() => localStorage.getItem(CP_KEY) || "");
  const [rango, setRango] = useState(10);
  const [tiendas, setTiendas] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = async (cpVal = cp, r = rango) => {
    const clean = (cpVal || "").replace(/\D/g, "");
    if (clean.length !== 5) { toast.error("Ingresa un código postal de 5 dígitos"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/tiendas/cercanas", { params: { cp: clean, rango: r } });
      setTiendas(data.tiendas);
      localStorage.setItem(CP_KEY, clean);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (cp.length === 5) buscar(cp, rango); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rango]);

  return (
    <div data-testid="comercios-cercanos" className="bg-white border border-zinc-200 rounded-sm p-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm bg-[#FF3366] text-white grid place-items-center shrink-0"><MapPin className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="font-display font-black text-xl tracking-tight">Seleccionar comercios cerca de tu ubicación</div>
          <p className="text-sm text-zinc-500 mt-0.5">Escribe tu código postal y te mostramos los comercios con C.P. cercano (ej. 45235 → 45225 a 45245).</p>
        </div>
      </div>
      <form onSubmit={e => { e.preventDefault(); buscar(); }} className="mt-4 flex flex-wrap gap-2 items-center">
        <Input data-testid="cp-input" value={cp} onChange={e => setCp(e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="Código postal" inputMode="numeric" maxLength={5} className="rounded-sm h-11 w-40 font-mono text-lg" />
        <div className="flex border border-zinc-200 rounded-sm overflow-hidden text-sm">
          {RANGOS.map(r => (
            <button type="button" key={r} data-testid={`cp-rango-${r}`} onClick={() => setRango(r)} className={`px-3 h-11 ${rango === r ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50"}`}>±{r}</button>
          ))}
        </div>
        <Button type="submit" data-testid="cp-buscar" disabled={loading} className="rounded-sm h-11 bg-zinc-900 hover:bg-[#FF3366]"><Search className="w-4 h-4 mr-1" /> {loading ? "Buscando…" : "Buscar"}</Button>
        {tiendas && tiendas.length > 0 && (
          <Link to={`/?cp=${cp}&rango=${rango}`} data-testid="cp-ver-productos" className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-[#FF3366] hover:underline">
            Ver productos de estos comercios <ArrowUpRight className="w-4 h-4" />
          </Link>
        )}
      </form>

      {tiendas && (
        <div className="mt-4">
          <div className="overline text-zinc-500 mb-2" data-testid="cp-result-count">{tiendas.length} comercio{tiendas.length === 1 ? "" : "s"} cerca del C.P. {cp}</div>
          {tiendas.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-sm p-6 text-center text-sm text-zinc-400">Aún no hay comercios con C.P. cercano. Prueba con un rango mayor.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {tiendas.map(t => (
                <Link key={t.id} to={`/t/${t.id}`} data-testid={`cercana-${t.id}`} className="card-lift flex gap-3 border border-zinc-200 rounded-sm p-3 bg-white">
                  <div className="w-14 h-14 rounded-sm bg-zinc-100 overflow-hidden grid place-items-center shrink-0">
                    {t.imagen_url ? <img src={t.imagen_url} alt={t.nombre_local} className="w-full h-full object-cover" /> : <StoreIcon className="w-5 h-5 text-zinc-300" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold tracking-tight truncate">{t.nombre_local}</div>
                    <div className="text-xs text-zinc-500 truncate">{t.categoria} · {t.colonia || t.ciudad}</div>
                    <div className="text-[11px] mt-1 font-mono"><span className="bg-[#D4FF00] px-1.5 py-0.5 rounded-sm">C.P. {t.codigo_postal}</span> <span className="text-zinc-400">{t.distancia_cp === 0 ? "mismo CP" : `±${t.distancia_cp}`} · {t.total_productos} art.</span></div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
